import React, { useEffect, useMemo, useState } from "react";
import MessengerLayout      from "../../layout/MessengerLayout";
import LeaveFilters         from "../../components/leave/LeaveFilters";
import LeaveTeamGrid        from "../../components/leave/LeaveTeamGrid";
import LeaveApprovalsQueue  from "../../components/leave/LeaveApprovalsQueue";
import LeaveCalendar        from "../../components/leave/LeaveCalendar";
import MemberLeaveDrawer    from "../../components/leave/MemberLeaveDrawer";
import dayjs from "dayjs";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

async function apiFetch(path) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function TeamLeavePage() {
  const [managerId, setManagerId] = useState(null);
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "null");
      if (u?.id) setManagerId(u.id);
    } catch {}
  }, []);

  const [view, setView] = useState("list");
  const [filters, setFilters] = useState({
    from:          new Date().toISOString().slice(0, 10),
    to:            new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    type:          "ALL",
    status:        "ALL",
    onlyConflicts: false,
    search:        "",
  });

  const [rows,     setRows]     = useState([]);
  const [queue,    setQueue]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [selected, setSelected] = useState(null);

  // Calendar month for calendar view
  const [calendarMonth, setCalendarMonth] = useState(dayjs());

  useEffect(() => {
    if (!managerId) return;
    let active = true;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        if (view === "list" || view === "calendar") {
          const data = await apiFetch(
            `/api/manager/leave/range?manager_id=${managerId}&from=${filters.from}&to=${filters.to}`
          );
          if (!active) return;
          setRows(Array.isArray(data) ? data : data?.items || []);

        } else if (view === "approvals") {
          const data = await apiFetch(
            `/api/manager/leave/approvals?manager_id=${managerId}`
          );
          if (!active) return;
          setQueue(Array.isArray(data) ? data : data?.items || []);
        }
      } catch (e) {
        if (!active) return;
        setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["leave_apply", "leave_cancel", "leave_approve", "leave_reject"].includes(t)) {
        run();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => {
      active = false;
      window.removeEventListener("kira_action_complete", handleKiraAction);
    };
  }, [managerId, view, filters.from, filters.to]);

  // KPIs
  const kpis = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      onLeaveToday: rows.filter(r => r.status !== "REJECTED" && r.from <= today && r.to >= today).length,
      upcoming7:    rows.filter(r => r.from > today && (new Date(r.from) - new Date(today)) / 86400000 <= 7).length,
      pending:      rows.filter(r => r.status === "PENDING").length,
      conflicts:    rows.filter(r => r.conflicts > 0).length,
      lowBalance:   rows.filter(r => typeof r.balanceLeft === "number" && r.balanceLeft <= 3).length,
    };
  }, [rows]);

  // Client-side filter
  const visibleRows = useMemo(() => {
    let out = [...rows];
    if (filters.type !== "ALL")   out = out.filter(r => (r.type || "").toUpperCase() === filters.type);
    if (filters.status !== "ALL") out = out.filter(r => r.status === filters.status);
    if (filters.onlyConflicts)    out = out.filter(r => r.conflicts > 0);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      out = out.filter(r => r.name?.toLowerCase().includes(q) || r.role?.toLowerCase().includes(q));
    }
    return out;
  }, [rows, filters]);

  const handleApprove = async (id, comment) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_BASE}/api/leave/${id}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ comment }),
      });
    } finally {
      setQueue(prev => prev.map(x => x.id === id ? { ...x, status: "APPROVED" } : x));
      setRows(prev  => prev.map(x => x.id === id ? { ...x, status: "APPROVED" } : x));
    }
  };

  const handleReject = async (id, comment) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_BASE}/api/leave/${id}/reject`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ comment }),
      });
    } finally {
      setQueue(prev => prev.map(x => x.id === id ? { ...x, status: "REJECTED" } : x));
      setRows(prev  => prev.map(x => x.id === id ? { ...x, status: "REJECTED" } : x));
    }
  };

  // Build leave history format for calendar (use rows as proxy)
  const calendarLeave = rows.map(r => ({
    leave_type: r.type,
    start_date: r.from,
    end_date:   r.to,
  }));

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Team Leave</h1>
          <p className="text-white/40 text-sm mt-0.5">Manage and approve your team's leave requests</p>
        </div>

        {/* Filters */}
        <LeaveFilters
          view={view}
          setView={setView}
          filters={filters}
          setFilters={setFilters}
          kpis={kpis}
        />

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>
        )}

        {/* Content */}
        <div className={`${card} p-5`}>
          {view === "list" && (
            <LeaveTeamGrid
              loading={loading}
              rows={visibleRows}
              onRowClick={r => setSelected(r)}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          )}

          {view === "approvals" && (
            <LeaveApprovalsQueue
              loading={loading}
              items={queue}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          )}

          {view === "calendar" && (
            <LeaveCalendar
              calendarMonth={calendarMonth}
              setCalendarMonth={setCalendarMonth}
              leaveHistory={calendarLeave}
              loading={loading}
            />
          )}
        </div>

      </div>

      {/* Drawer */}
      {selected && (
        <MemberLeaveDrawer
          member={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </MessengerLayout>
  );
}